import { SingleBitPerPixelBitmap } from "../printing/barcodes/pdf417";

// the pdf 1.1 spec: https://ia802303.us.archive.org/3/items/Portable-Pocument-Format-Reference-Manual-Version-1.1.ISBN_0-201-62628-4/Portable-Pocument-Format-Reference-Manual-Version-1.1.ISBN_0-201-62628-4.pdf


// produce a string which encodes a pdf1.1 compliant pdf file.
// the contents of the pdf are determined by pages of 'objects'.
// all objects have a 'type', and a left/bottom coordinate.
// other data provided is dependent on the type, documented in the definition of PDFObject, below.
export default function generatePDF(width: number, height: number, pages: PDFPage[]): string {
  // @TODO assert pages.length !== 0
  
  // @TODO add explicit '/UserUnit 1.0     % default of 72 units/inch
  // /MediaBox [ 0 0 612 792 ]             % 8.5 x 11 inches, US Letter size
  // /MediaBox [ 0 0 595 842 ]             % 21 x 29.7 centimeters, A4 paper

  const pageObjectIndices: number[] = [];
  {
    let blocksSoFar = 3;
    for (let i = 0; i < pages.length; i++) {
      pageObjectIndices.push(blocksSoFar);
      blocksSoFar += pages[i].objects.length + 1; // +1 for the page itself
    }
  }

  let pdf: string = 
`%PDF-1.1
1 0 obj
  <<
    /Type /Catalog
    /Pages 2 0 R
  >>
endobj
2 0 obj
  <<
    /Type /Pages
    /Kids [ ${pageObjectIndices.map(i => `${i} 0 R`).join(" ")} ]
    /Count ${pages.length}
    /MediaBox [0 0 ${width} ${height}]
  >>
endobj
`;

  // start at 2, 1 for the Catalog Object, and 1 for the 'Pages' object, which are always present.
  const totalNumObjects: number = pages.reduce((acc, curr) => acc + curr.objects.length + 1, 2);

  // note that the lines in the xref table need to be exactly 20 bytes long, including the newline character.
  // this means the trailing space in each line below of the xref table is important!
  let xref: string = 
`xref
0 ${totalNumObjects}
0000000000 65535 f 
0000000009 00000 n 
0000000070 00000 n 
`;

  // all subsequent objects we'll need to calculate their xrefs as we go - they will be highly variable.
  let nextObjXref: number = pdf.length;
  let nextObjectIndex = 3; // start at 3 because first 2 are taken by the Catalog and Pages objects.
  pages.forEach(page => {

    {
      // write this page object and the xref for it into the xref table:
      const pageObject = makePage(nextObjectIndex++, page);
      pdf += pageObject;
      xref += `${`${nextObjXref}`.padStart(10, "0")} 00000 n \n`;
      nextObjXref += pageObject.length;  
    }

    page.objects.forEach(object => {
      let objectData: string | undefined;
      switch (object.type) {
        case PDFObjectType.text:
          objectData = makeTextObject(
            nextObjectIndex++,
            object.left,
            object.bottom,
            object.text,
            object.fontSize,
            object.color,
            object.bold,
          );
          break;

        case PDFObjectType.singleBitPerPixelImage:
          objectData = makeSingleBitPerPixelImageObject(
            nextObjectIndex++,
            object.bitmap,
            object.left,
            object.bottom,
            object.width,
            object.height,
            object.rotateRadians ?? 0
          );
          break;

        case PDFObjectType.rect:
          objectData = makeRectObject(
            nextObjectIndex++,
            object.left,
            object.bottom,
            object.width,
            object.height,
            object.color,
            object.strokeWidth,
            object.strokeColor,
          );
          break;
      }

      pdf += objectData;

      // write the xref for this new object into the xref table:
      xref += `${`${nextObjXref}`.padStart(10, "0")} 00000 n \n`;
  
      nextObjXref += objectData.length;
    });
  });

  // we're done adding stuff to the pdf, we can finalize the xref table and then concat it with the pdf itself, done.
  xref += 
`trailer
  <<
    /Root 1 0 R
    /Size ${totalNumObjects}
  >>
startxref
${nextObjXref}
%%EOF`;

  pdf += xref;

  return pdf;
}

export function inchesToPDFUnits(inches: number): number {
  return inches*72;
}
export function pdfUnitsToInches(pdfUnits: number): number {
  return pdfUnits/72;
}

export interface PDFPage {
  objects: PDFObject[];
}

export enum PDFObjectType {
  text,
  singleBitPerPixelImage,
  rect
}

export interface PDFObject {
  type: PDFObjectType;

  // text, rect and image types:
  left?: number, bottom?: number;

  color?: [number, number, number]; // optional rgb color, normalized 0..1, default black, used for fill text/rect

  // for image and rect type (for text the width/height is implicit depending on the text itself):
  width?: number;
  height?: number;

  // for rects:
  strokeColor?:  [number, number, number]; // optional rgb color, normalized 0..1, default black
  strokeWidth?: number;

  // for the text type: (all text is either Helvetica or Helvetica Bold right now...)
  text?: string;
  fontSize?: number; // optional, in pt (or PDF units)
  bold?: boolean; // whether or not the text should be bold.

  // for the image type:
  bitmap?: SingleBitPerPixelBitmap
  rotateRadians?: number; // optional
}

function makePage(index: number, page: PDFPage) {
  const pageObject: string = 
`${index} 0 obj
  <<
    /Type /Page
    /Parent 2 0 R
    /Resources
    << 
      /Font
      << /F1
        <<
          /Type /Font
          /Subtype /Type1
          /BaseFont /Helvetica
        >>
      >>
    >>
    /Contents [ ${page.objects.map((_, objectIndex) => `${index + objectIndex + 1} 0 R`).join(" ")} ]
  >>
endobj
`;
  return pageObject;
};

// just a colored and filled rectangle (optional stroke as well, so you can have hollow-bordered rects by setting the fill color to your background color)
function makeRectObject(
  index: number,
  left: number,
  bottom: number,
  width: number,
  height: number,
  color: [ number, number, number ] = [ 0, 0, 0 ], // rgb color, normalized 0..1, default black
  strokeWidth: number = 0,
  strokeColor: [ number, number, number ] = [ 0, 0, 0 ], // rgb color, normalized 0..1, default black
) {
  const rectObject: string = 
`${index} 0 obj
  << >>
stream
  ${strokeWidth} w
  ${strokeColor.join(" ")} RG
  ${color.join(" ")} rg
  ${left} ${bottom} ${width} ${height} re
  ${strokeWidth > 0 ? "b" : "f\nn"}
endstream
endobj
`;
  return rectObject;
};

// text can be multi-line optionally.
// we only support left-alignment though right now.
// font assumed to be Helvetica
// line-height is font size + 2 unit padding (TL command)
function makeTextObject(
  index: number,
  left: number,
  bottom: number,
  text: string,
  fontSize: number = 12,
  color: [ number, number, number ] = [ 0, 0, 0 ], // rgb color, normalized 0..1, default black
  bold: boolean = false
) {
  /*
  These are the escape-able characters in a bit of pdf text.
  Notable is the parens - we need to replace those. Text is wrapped in parens in pdf
  in order to delimit it.
  
  Backslashes on their own are problematic too, as if by chance we receive a string like:

    "Lego Amelia Earheart Tribute \40450 \ Exclusive Building Set"

    There are two backslashes in the above string. The latter backslash has no effect, because it is followed by an invalid
    character to be escaped - a space (this happens will all characters I've tried that are not in the table below). 
    However, the former backslash is followed by 3 octal digits, which causes it to be interpreted as an octal character
    - not what we want. So we handle backslashes in all cases by escaping them/treating them as true backslashes.

  Sequence | Meaning
  ---------------------------------------------
  \n       | LINE FEED (0Ah) (LF)
  \r       | CARRIAGE RETURN (0Dh) (CR) 
  \t       | HORIZONTAL TAB (09h) (HT)
  \b       | BACKSPACE (08h) (BS)
  \f       | FORM FEED (FF)
  \(       | LEFT PARENTHESIS (28h)
  \)       | RIGHT PARENTHESIS (29h)
  \\       | REVERSE SOLIDUS (5Ch) (Backslash)
  \ddd     | Character code ddd (octal)
  */
  const escaped = text.trim().replaceAll("\\", "\\\\").replaceAll("(", "\\(").replaceAll(")", "\\)");
  const splitted = escaped.split("\n");

  let textObject: string = 
`${index} 0 obj
  << >>
stream
  BT
    ${color.join(" ")} rg
    ${bold ? "/F1" : "/F1"} ${fontSize} Tf
    ${fontSize} TL
    ${left} ${bottom} Td
`;

  splitted.forEach((line, index) => {
    if (index === 0) {
      textObject += `(${line}) Tj\n`;
    } else {
      textObject += `(${line}) '\n`;
    }
  })

  textObject +=  
  `ET
endstream
endobj
`;

  return textObject;
};

// @NOTE(nick) longer-term, if generation of pdfs is something seven tools wants to do more of, we might want
// to modify this to work with more image types.
// the image component is actually a 1-bit-per-pixel, grayscale, 1-dimensional image, encoded 
// as an ascii hex string. (pdf spec call this ASCIIHexDecode). We vertically stretch the image
// with a transformation matrix inside the pdf to give the appropriate appearance.
function makeSingleBitPerPixelImageObject(
  index: number,
  bitmap: SingleBitPerPixelBitmap,
  left: number,
  bottom: number,
  width: number,
  height: number,
  rotateRadians: number = 0
): string {
  const bitmapObject: string =
`${index} 0 obj
  <<
  >>
stream
q
${width} ${0} ${0} ${height} ${left} ${bottom} cm
${(Math.cos(rotateRadians)).toFixed(2)} ${Math.sin(rotateRadians).toFixed(2)} ${-Math.sin(rotateRadians).toFixed(2)} ${(Math.cos(rotateRadians)).toFixed(2)} ${0} ${0} cm
BI
/W ${bitmap.width}
/H ${bitmap.height}
/CS /G
/BPC 1
/F /AHx
ID
${uint8ArrayToHex(bitmap.data)}>
EI
Q
endstream
endobj
`;
  return bitmapObject;
};

// these are just some tables for use in the buffer-to-hex conversion below:
const LUT_HEX_4b = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F'];
const LUT_HEX_8b = new Array(0x100);
for (let n = 0; n < 0x100; n++) {
  LUT_HEX_8b[n] = `${LUT_HEX_4b[(n >>> 4) & 0xF]}${LUT_HEX_4b[n & 0xF]}`;
}
// this is a fast way to encode a Uint8Array as hex according to this: https://www.xaymar.com/articles/2020/12/08/fastest-uint8array-to-hex-string-conversion-in-javascript/
// in nodejs we have Buffer.from(array).toString("hex"), but not on the client!
function uint8ArrayToHex(buffer: Uint8Array) {
  let out = '';
  for (let idx = 0, edx = buffer.length; idx < edx; idx++) {
    out += LUT_HEX_8b[buffer[idx]];
  }
  return out;
}

// note measurements of the TextMetrics object are all in css pixels, but PDFs generally work in their
// own unit space - we try and enforce (the default of PDF) 1 pdf units === 1 pt. 1 css px === 0.75pt.
//
// WARNING - the canvas API does dumb things if you pass it text that is multi-line! It appears to sum
// the width of the lines, for example, to produce the total width (this is obviously wrong, the width
// of the text is the width of the longest-line). It's recommended to split your lines before trying to
// measure them.
//
// Further warning: you might want to do `await document.fonts.ready;` before calling this. We don't,
// because we currently only use this with Helvetica, a font which is always loaded AFAIK.
//
export function measureText(text: string, font: string = "helvetica", fontSize: string = "12pt", bold: boolean = false): TextMetrics {
  // @ts-ignore 
  const canvas: HTMLCanvasElement = measureText.canvas || (measureText.canvas = document.createElement("canvas"));
  const context = canvas.getContext("2d");
  context.font = `${bold ? "bold " : ""}${fontSize} ${font}`; // canvas api forces you to do this to set font-size and boldness... bleh
  const metrics = context.measureText(text);
  return metrics;
}

// note that because 1px is defined as a pixel at DPI === 96, and monitors have different DPI,
// and pdf uses 'pt' units instead, this table is often wrong, but usually only slightly. 
// the values in this table are in pixels, but we can assume they are equivalent to pt == 12
// in most cases.
// @NOTE(nick) @DEPRECATED use the 'measureTextWidth' above instead...
export const helveticaWidthTable: { [key: string]: number } = {
  "0": 8.9,
  "1": 7.728499755859375,
  "2": 8.9,
  "3": 8.9,
  "4": 8.9,
  "5": 8.9,
  "6": 8.9,
  "7": 8.9,
  "8": 8.9,
  "9": 8.9,
  " ": 4.45,
  "!": 4.45,
  "\"": 5.683333129882812,
  "#": 8.9,
  "$": 8.9,
  "%": 14.233333740234375,
  "&": 10.666666259765625,
  "'": 3.05,
  "(": 5.333333129882813,
  ")": 5.333333129882813,
  "*": 6.233333129882812,
  "+": 9.35,
  ",": 4.45,
  "-": 5.333333129882813,
  ".": 4.45,
  "/": 4.45,
  ":": 4.45,
  ";": 4.45,
  "<": 9.35,
  "=": 9.35,
  ">": 9.35,
  "?": 8.9,
  "@": 16.25,
  "A": 10.666666259765625,
  "B": 10.666666259765625,
  "C": 11.55,
  "D": 11.55,
  "E": 10.666666259765625,
  "F": 9.766666870117188,
  "G": 12.45,
  "H": 11.55,
  "I": 4.45,
  "J": 8,
  "K": 10.666666259765625,
  "L": 8.9,
  "M": 13.333333740234375,
  "N": 11.55,
  "O": 12.45,
  "P": 10.666666259765625,
  "Q": 12.45,
  "R": 11.55,
  "S": 10.666666259765625,
  "T": 9.766666870117188,
  "U": 11.55,
  "V": 10.666666259765625,
  "W": 15.1,
  "X": 10.666666259765625,
  "Y": 10.666666259765625,
  "Z": 9.766666870117188,
  "[": 4.45,
  "\\": 4.45,
  "]": 4.45,
  "^": 7.5,
  "_": 8.9,
  "`": 5.333333129882813,
  "a": 8.9,
  "b": 8.9,
  "c": 8,
  "d": 8.9,
  "e": 8.9,
  "f": 4.169500122070312,
  "g": 8.9,
  "h": 8.9,
  "i": 3.55,
  "j": 3.55,
  "k": 8,
  "l": 3.55,
  "m": 13.333333740234375,
  "n": 8.9,
  "o": 8.9,
  "p": 8.9,
  "q": 8.9,
  "r": 5.333333129882813,
  "s": 8,
  "t": 4.45,
  "u": 8.9,
  "v": 8,
  "w": 11.55,
  "x": 8,
  "y": 8,
  "z": 8,
  "{": 5.35,
  "|": 4.15,
  "}": 5.35,
  "~": 9.35
};

// const generateFontWidthTable = (fontFamily: string = "helvetica", fontSize: string = "12pt") => {
//   var d1 = document.createElement("div");
//   d1.id = "test3";
//   d1.innerHTML = "test";
//   // @ts-ignore
//   d1.style = `width: fit-content; padding: 0px; margin: 0px; font-family: ${fontFamily}; font-size: ${fontSize} !important`;
//   document.documentElement.appendChild(d1);

//   const table = {};
//   var N = 100;
//   for (var i = 32; i < 127; i++) {
//     var s = String.fromCharCode(i).replace(" ","&nbsp;");
//     d1.innerHTML = s.repeat(N);
//     var width = d1.getBoundingClientRect().width;
//     table[s] = width/N;
//   }
//   console.log(d1.style);
//   console.log(table);
// }

//@ts-ignore
// window.generateFontWidthTable = generateFontWidthTable;
 
/*
  copy-pasting the graphics operators here because I forget them and they are annoying to find:

  Color operators:
    G - stroke color
    rg - fill color
  
  Path operators:
    re - path rectangle

  Path painting operators:
    n - ends the path without filling or stroking it
    S - strokes the path
    s - closes the path then strokes it
    f - fills the path, using the non-zero winding number rule to determine the region to fill
    F - same as F, included for backwards compatability
    f* - fills the path, using the even-odd rule to determine the region to fill
    B - fill and stroke
    b - closepath, fill and stroke
    B* - eofill (f*) and stroke
    b* - closepath, eofill, and stroke
*/
